/**
 * Website Compliance Scan - Unified Workflow Types
 *
 * These types define the unified compliance scan workflow that orchestrates
 * both website scraping and compliance analysis in a single process.
 */

import type { ScrapedDataStatus } from '../../prisma/enums';
import type { ComplianceAnalysisProgress } from './compliance-result';

/**
 * Status of a compliance scan workflow.
 * Tracks progress through scraping and analysis phases.
 */
export type ComplianceScanStatus =
    | 'PENDING' // Just created, waiting to start
    | 'SCRAPING' // Website scraping in progress
    | 'SCRAPE_FAILED' // Scraping failed
    | 'ANALYZING' // Compliance analysis in progress
    | 'COMPLETED' // Successfully completed
    | 'FAILED' // Analysis failed
    | 'CANCELLED'; // User cancelled

/**
 * Progress tracking for a compliance scan.
 * Provides detailed progress information across both phases.
 */
export interface ComplianceScanProgress {
    /** Current phase of the scan workflow */
    currentPhase: 'QUEUED' | 'SCRAPING' | 'ANALYZING' | 'COMPLETED' | 'ERROR';

    /** Scraping phase progress details */
    scrapingProgress?: {
        pagesDiscovered: number;
        pagesScraped: number;
        currentUrl?: string;
    };

    /** Analysis phase progress details (reuses existing type) */
    analysisProgress?: ComplianceAnalysisProgress;

    /** When the scan started */
    startedAt?: Date;

    /** When scraping completed */
    scrapingCompletedAt?: Date;

    /** When the entire scan completed */
    completedAt?: Date;

    /** Last progress update timestamp */
    lastUpdated: Date;
}

/**
 * Row contract returned by websiteChecker.listScrapedData.
 * Shared by the router, table consumers and browser fixtures.
 */
export interface ScrapedDataListRow {
    id: bigint;
    url: string;
    status: ScrapedDataStatus;
    pagesScraped: number;
    createdAt: Date;
    updatedAt: Date;
    error: string | null;
    complianceScanId: bigint | null;
    complianceScanStatus: ComplianceScanStatus | null;
    complianceScanProgress: ComplianceScanProgress | null;
    companyName: string | null;
    scanType: ComplianceScanType | null;
}

/**
 * Type of compliance scan based on crawl depth.
 */
export type ComplianceScanType = 'QUICK' | 'FULL' | 'SINGLE_PAGE';

/**
 * Labels for compliance scan types.
 */
export const SCAN_TYPE_LABELS: Record<ComplianceScanType, string> = {
    QUICK: 'Quick scan',
    FULL: 'Full scan',
    SINGLE_PAGE: 'Single page',
};

/**
 * Labels for compliance scan statuses.
 */
export const COMPLIANCE_SCAN_STATUS_LABELS: Record<ComplianceScanStatus, string> = {
    PENDING: 'Queued',
    SCRAPING: 'Scanning Website',
    SCRAPE_FAILED: 'Scan Failed',
    ANALYZING: 'Analysing Compliance',
    COMPLETED: 'Completed',
    FAILED: 'Failed',
    CANCELLED: 'Cancelled',
};
